import { CompanyService } from '@core/services/company.service';
import { Component, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Company } from '@core/models/company';
import { User } from '@core/models/user';
import { CompanyInviteService } from '@core/services/company-invite.service';
import { ThemeService } from '@core/services/theme.service';
import { UserService } from '@core/services/user.service';
import { EmailTemplatesService } from '../../services/email-templates.service';
import { EmailTemplate } from '../../models/email-template';
import { EmailService } from '../../services/email.service';
import {
  COMPANY_INVITE_FALLBACK_TEMPLATE,
  COMPANY_INVITE_PLACEHOLDERS,
  COMPANY_INVITE_TEMPLATE_NAME,
} from '@core/config/email-templates';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { EmailVerificationService } from '@core/services/email-verification.service';

@Component({
  selector: 'app-company-invite-form',
  templateUrl: './company-invite-form.component.html',
  styleUrl: './company-invite-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
/**
 * Company invite form used during onboarding and in company management flows.
 *
 * This component depends on a valid company context (tenant) and sends one or more
 * invite emails after generating per-company invite codes.
 *
 * The email body is looked up in Firestore by the stable template name rather
 * than by document id, and falls back to a built-in default when that lookup
 * returns nothing — a missing template degrades the body, it does not remove
 * the feature (dashboard#863).
 */
export class CompanyInviteFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private themeService = inject(ThemeService);
  private userService = inject(UserService);
  private inviteService = inject(CompanyInviteService);
  private emailService = inject(EmailService);
  private emailTemplateService = inject(EmailTemplatesService);
  private router = inject(Router);
  private companyService = inject(CompanyService);
  private emailVerification = inject(EmailVerificationService);

  @Input() skipStep: boolean = true;
  // @Input() redirectPath: string[] = ['/register/select-plan'];
  @Input() redirectPath: string[] = ['/logging-in'];

  confirm: boolean = false;
  inviteForm!: FormGroup;
  user?: User | any;
  company?: Company;
  emails?: string[];
  message: any;
  emailMessage!: EmailTemplate;

  /**
   * Inline error shown above the invite actions when a send fails.
   *
   * Rendered as an inline alert rather than a modal: per CLAUDE.md a failure
   * message is a toast or an inline error. An inline banner is used here
   * because this partial also renders inside the sign-up flow, which has no
   * `app-toast-container` in its layout — a toast there would be swallowed.
   */
  inviteError: string | null = null;

  /**
   * True when the invite was stopped because the sender's email address is
   * not verified yet (#943, #956): shown as a "verify first" notice with a
   * resend button rather than as an error.
   */
  inviteNeedsVerification = false;

  /** Result of "Send a new link" in that notice. */
  verificationResend: 'idle' | 'sending' | 'sent' | 'failed' = 'idle';

  constructor() {
    this.themeService.showSpinner();
    this.getCompanyInformation();
    void this.loadHTML();
  }

  ngOnInit(): void {
    this.inviteForm = this.fb.group({
      emails: ['', Validators.required],
      message: '',
    });
  }

  getCompanyInformation() {
    this.userService.getUserModel().subscribe((user: User | null) => {
      if (user) {
        this.user = user;
        const currentCompanyId = user.currentCompanyId || (user as any).currentCompanyId;
        if (!currentCompanyId) {
          console.error('CompanyInviteForm: user has no currentCompanyId/currentCompanyId');
          this.themeService.hideSpinner();
          return;
        }

        this.companyService
          .getDocument(currentCompanyId)
          .then((company: Company) => {
            this.company = company;
            this.themeService.hideSpinner();
          })
          .catch((error: any) => {
            console.error('CompanyInviteForm: failed to load company', error);
            this.themeService.hideSpinner();
          });
      }
    });
  }

  confirmData() {
    if (this.inviteForm.valid) {
      this.confirm = true;
      this.inviteError = null;
      const emailControl = this.inviteForm.get('emails');
      const messageControl = this.inviteForm.get('message');
      if (messageControl && emailControl && this.company) {
        this.emails = emailControl.value
          .split(',')
          .map((email: string) => email.trim());
        this.message = messageControl.value;
      }
    }
  }

  /**
   * Generates invite codes and sends invite emails for all entered recipients.
   *
   * Always resolves UI state (spinner) and always reports an outcome: every
   * early return and every thrown error now sets `inviteError`, because a
   * sender who is told nothing assumes the invite went out (dashboard#863).
   */
  async onSubmitInvite() {
    if (!this.inviteForm.valid) {
      return;
    }

    this.inviteError = null;
    this.inviteNeedsVerification = false;

    // Inviting teammates needs a verified email address (#943). The rules
    // refuse the invite otherwise; say why instead of "permission denied".
    if (this.emailVerification.isUnverified() && !(await this.emailVerification.refresh())) {
      this.inviteNeedsVerification = true;
      this.verificationResend = 'idle';
      this.inviteError =
        'Verify your email address to invite teammates. Open the link we emailed you, or send a new one.';
      return;
    }

    this.themeService.showSpinner();

    try {
      const emailControl = this.inviteForm.get('emails');
      const messageControl = this.inviteForm.get('message');

      if (!messageControl || !emailControl) {
        this.inviteError =
          'Something went wrong preparing the invite. Refresh the page and try again.';
        return;
      }

      if (!this.company?.uid) {
        console.error('CompanyInviteForm: company context not available');
        this.inviteError =
          'We could not determine which company to invite to. Refresh the page and try again.';
        return;
      }

      // Ensure an email template is loaded before attempting sends. loadHTML()
      // always leaves a usable body in place, so this cannot be empty.
      if (!this.emailMessage?.content) {
        await this.loadHTML();
      }
      if (!this.emailMessage?.content) {
        console.error('CompanyInviteForm: email template not available');
        this.inviteError =
          'The invitation email template could not be loaded, so no invites were sent. Please try again or contact support.';
        return;
      }

      const rawEmails = String(emailControl.value ?? '');
      this.emails = rawEmails
        .split(/[\n,]+/)
        .map((email: string) => email.trim())
        .filter(Boolean);

      if (!this.emails.length) {
        this.inviteError = 'Enter at least one email address to invite.';
        return;
      }

      this.message = messageControl.value;

      const currentCompanyId = this.company.uid;
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 7);
      const expirationISOString = expirationDate.toISOString();

      // Send every invite and wait for each outcome. Promise.all would reject
      // on the first failure while other non-idempotent sends were still in
      // flight, so the sender could not tell which invites actually went out.
      const recipients = this.emails;
      const results = await Promise.allSettled(
        recipients.map(async (email: string) => {
          const inviteCode = await this.inviteService.generateOrUpdateInviteCode(
            currentCompanyId,
            email,
            expirationISOString,
          );

          const emailContent = this.getEmailContent(
            this.emailMessage.content,
            email,
            inviteCode,
            this.message,
          );

          await firstValueFrom(this.emailService.sendCompanyInvite(email, emailContent));
        }),
      );

      const failed = recipients.filter((_, i) => results[i].status === 'rejected');
      if (failed.length) {
        console.error(
          `CompanyInviteForm: ${failed.length} of ${recipients.length} invite(s) failed`,
          results.filter((r) => r.status === 'rejected').map((r) => (r as PromiseRejectedResult).reason),
        );
        this.inviteError = this.describeSendFailure(failed, recipients.length);
        // Leave only the failed addresses in the form so a retry cannot send
        // a duplicate invite to anyone who already received one.
        emailControl.setValue(failed.join(', '));
        this.emails = failed;
        return;
      }

      await this.router.navigate(this.redirectPath);
    } catch (error: any) {
      console.error('CompanyInviteForm: invite send failed', error);
      this.inviteError =
        'Something went wrong while sending the invitations, and some may have been delivered. Check with the recipients before retrying, or contact support.';
    } finally {
      this.themeService.hideSpinner();
    }
  }

  /**
   * Builds the inline error for a send where some or all recipients failed.
   *
   * @param failed Recipient addresses whose invite was not sent.
   * @param total Number of recipients attempted.
   * @returns User-facing message naming the failed recipients.
   */
  private describeSendFailure(failed: string[], total: number): string {
    const list = failed.join(', ');
    if (failed.length === total) {
      return `We could not send the invitations to ${list}. Please try again or contact support.`;
    }
    return `${total - failed.length} of ${total} invitations were sent, but we could not send to ${list}. Only the failed addresses remain in the form — retry to send just those.`;
  }

  /**
   * Dismisses the inline invite error banner.
   */
  /** Sends a new verification link from the "verify first" notice. */
  async resendVerification(): Promise<void> {
    if (this.verificationResend === 'sending') return;
    this.verificationResend = 'sending';
    this.verificationResend = (await this.emailVerification.resend()) ? 'sent' : 'failed';
  }

  dismissInviteError(): void {
    this.inviteNeedsVerification = false;
    this.inviteError = null;
  }

  /**
   *
   * @param emailMessage string
   * @param email string
   * @param inviteCode string
   */
  getEmailContent(
    emailMessage: string,
    email: any,
    inviteCode: any,
    customMessage: string,
  ): string {
    if (this.user && this.company) {
      const sender = `${this.user.displayName} (${this.user.email})`;
      const organization = this.company.name;
      const webUrl = environment.app_url; //'https://dev.dashboard.scanninja.app/';

      // Every value is HTML-escaped: the sender, organization and custom
      // message are user-controlled and the result is mailed from the Scan
      // Ninja identity, so unescaped values would let a sender inject markup
      // or links. A replacer function (not a string) keeps `$&`/`$1`-style
      // sequences in the values literal.
      const values: Record<string, string> = {
        '{{INVITE_CODE}}': inviteCode,
        '{{SENDER}}': sender,
        '{{CUSTOM_MESSAGE}}': customMessage,
        '{{ORGANIZATION}}': organization,
        '{{WEB_URL}}': webUrl,
      };
      return emailMessage.replace(
        /\{\{(?:INVITE_CODE|SENDER|CUSTOM_MESSAGE|ORGANIZATION|WEB_URL)\}\}/g,
        (token: string) => escapeHtml(values[token]),
      );
    } else {
      throw new Error('Internal Server Error: 500'); // Throwing an error with code 500
    }
  }

  /**
   * Loads the invite email template from Firestore by its stable name, falling
   * back to the template bundled with the app.
   *
   * Looking the document up by `name` rather than by a hardcoded auto-generated
   * id is what makes one build work against every Firebase project; the
   * fallback is what keeps invites working when the collection has not been
   * seeded at all.
   *
   * @returns Promise resolving once a usable template is in place.
   */
  async loadHTML(): Promise<void> {
    let template: EmailTemplate | null = null;

    try {
      template = await this.emailTemplateService.getTemplateByName(
        COMPANY_INVITE_TEMPLATE_NAME,
      );
    } catch (error: any) {
      console.error('CompanyInviteForm: failed to load email template', error);
    }

    const missing = template?.content
      ? COMPANY_INVITE_PLACEHOLDERS.filter((p) => !template!.content.includes(p))
      : [];
    if (template?.content && !missing.length) {
      this.emailMessage = template;
      return;
    }

    console.warn(
      template?.content
        ? `CompanyInviteForm: email template "${COMPANY_INVITE_TEMPLATE_NAME}" is missing ${missing.join(', ')}; using the built-in default.`
        : `CompanyInviteForm: email template "${COMPANY_INVITE_TEMPLATE_NAME}" not found in Firestore; using the built-in default.`,
    );
    this.emailMessage = new EmailTemplate({
      uid: '',
      name: COMPANY_INVITE_TEMPLATE_NAME,
      content: COMPANY_INVITE_FALLBACK_TEMPLATE,
    });
  }
}

/**
 * Escapes a value for safe interpolation into HTML text or a quoted attribute.
 *
 * @param value Raw value; `null`/`undefined` become an empty string.
 * @returns The value with `& < > " '` replaced by entities.
 */
function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
